import { createSelector } from '@reduxjs/toolkit';

import { Line, LineDirectory, Station } from '@paragon/contract-library';

import { RootState } from '../store';

export interface DirectoryViewModel {
  type: 'directory';
  source: LineDirectory;

  children: {
    subdirectories: {
      [guid: string]: DirectoryViewModel;
    };

    lines: {
      [guid: string]: LineViewModel;
    };
  };
}

export interface LineViewModel {
  type: 'line';
  source: Line;

  children: {
    stations: {
      [guid: string]: InsertedStationViewModel;
    };
  };
}

export interface StationViewModel {
  type: 'station';
  source: Station;

  parents: LineViewModel[];
}

export interface InsertedStationViewModel extends StationViewModel {
  parent: LineViewModel;

  xIndex: number;
  yIndex: number;
}

export type HolisticViewModel = {
  flatView: {
    [guid: string]: AnyHierarchicalViewModel;
  };

  hierarchicalView: {
    type: 'holistic';

    children: {
      directories: {
        [guid: string]: DirectoryViewModel;
      };

      lines: {
        [guid: string]: DirectoryViewModel;
      };
    };
  };
};

export type AnyHierarchicalViewModel =
  HolisticViewModel['hierarchicalView'] | DirectoryViewModel | LineViewModel | StationViewModel;

export const getHolisticViewModel = createSelector(
  (state: RootState) => state.lines,
  (state: RootState) => state.stations,

  (linesState, stationsState): HolisticViewModel | 'loading' | 'error' => {
    const statuses = [linesState.status, stationsState.status];

    if (statuses.includes('catastrophicallyFailed')) return 'error';
    if (statuses.includes('loading')) return 'loading';

    const result: HolisticViewModel = {
      flatView: {},
      hierarchicalView: { type: 'holistic', children: { lines: {}, directories: {} } },
    };

    const lines = Object.values(linesState.lines);
    const stations = Object.values(stationsState.stations);
    const directories = Object.values(linesState.directories);

    // Pass 1: create models

    for (const station of stations) {
      result.flatView[station.guid] = { type: 'station', source: station, parents: [] };
    }

    for (const line of lines) {
      result.flatView[line.guid] = { type: 'line', source: line, children: { stations: {} } };
    }

    for (const directory of directories) {
      result.flatView[directory.guid] = {
        type: 'directory',
        source: directory,
        children: { subdirectories: {}, lines: {} },
      };
    }

    // Pass 2: set up relationships

    for (const [children, topLevelPropertyName, nestedPropertyName] of [
      [lines, 'lines', 'lines'],
      [directories, 'directories', 'subdirectories'],
    ] as const) {
      for (const child of children) {
        const parent: any = child.parentGuid ? result.flatView[child.parentGuid] : result.hierarchicalView;
        const relationshipName = child.parentGuid ? nestedPropertyName : topLevelPropertyName;

        parent.children[relationshipName][child.guid] = result.flatView[child.guid];
      }
    }

    // Station insertions are stored separately (they come from a join table)
    for (const [lineGuid, stationInsertions] of Object.entries(linesState.stationInsertions)) {
      for (const insertion of stationInsertions) {
        const lineViewModel = result.flatView[lineGuid] as LineViewModel;
        const stationViewModel = result.flatView[insertion.guid] as StationViewModel;

        stationViewModel.parents.push(lineViewModel);

        lineViewModel.children.stations[insertion.guid] = {
          ...stationViewModel,
          parent: lineViewModel,
          xIndex: insertion.xIndex,
          yIndex: insertion.yIndex,
        };
      }
    }

    return result;
  },
);
